import React from "react";
import { View, Text, Image, TextInput, TouchableOpacity } from "react-native";
import { CreateResponsiveStyle, DEVICE_SIZES } from "rn-responsive-styles";
import { toast } from "react-toastify";
import { AppFontStyle } from "../../../../styles/AppFontStyle";
import { CommonButton } from "../../../../common/CommonButton";
import { AppColors } from "../../../../utility/AppColors";
import { AppImages } from "../../../../utility/AppImages";

interface StaticEventChatPreviewProps {
  deviceSize: DEVICE_SIZES;
  details: any;
  onChatTap: () => void;
  title: string;
  IsEventCanceled?: boolean;
  reason?: string;
}

const StaticEventChatPreview: React.FC<StaticEventChatPreviewProps> = ({
  deviceSize,
  details,
  onChatTap,
  title,
  IsEventCanceled = false,
  reason = "",
}) => {
  const styles = useStyles();

  const sampleMessages =
    details?.participantDetails?.length > 0
      ? details.participantDetails.slice(0, 2).map((participant, index) => ({
          messageContent: [
            "Excited for this event! 🎮",
            "This is going to be epic!",
          ][index % 2],
          userId: participant.userID,
          timestamp: new Date(Date.now() - (300000 - index * 60000)).getTime(),
          isUser: false,
        }))
      : [
          {
            messageContent: "Excited for this event! 🎮",
            userId: "GameMaster",
            timestamp: new Date(Date.now() - 300000).getTime(),
            isUser: false,
          },
          {
            messageContent: "This is going to be epic!",
            userId: "Organiser",
            timestamp: new Date(Date.now() - 240000).getTime(),
            isUser: false,
          },
          {
            messageContent: "Good luck everyone!",
            userId: "Challenger",
            timestamp: new Date(Date.now() - 120000).getTime(),
            isUser: false,
          },
        ];

  const StaticMessageItem = ({ message, isUser }) => {
    const backgroundColor = isUser
      ? AppColors.white
      : AppColors.appViolentColor_6D08C3;
    const messageTextColor = isUser ? AppColors.black : AppColors.white;
    const timestampColor = isUser ? "rgb(109, 8, 195)" : AppColors.white;

    const formattedTime = new Date(message.timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

    return (
      <View
        style={[
          styles.messageContainer,
          {
            alignSelf: isUser ? "flex-end" : "flex-start",
            backgroundColor,
          },
        ]}
      >
        <View
          style={[
            styles.header,
            { justifyContent: isUser ? "flex-end" : "space-between" },
          ]}
        >
          {!isUser && (
            <Text
              style={styles.userNameText}
              numberOfLines={1}
              ellipsizeMode="tail"
            >
              {message.userId}
            </Text>
          )}

          <Text style={[styles.timestampText, { color: timestampColor }]}>
            {formattedTime}
          </Text>
        </View>
        <Text
          style={[{ color: messageTextColor }, AppFontStyle.REGULAR_ANYBODY_14]}
        >
          {message.messageContent}
        </Text>
      </View>
    );
  };

  const StaticNoMessages = () => (
    <View>
      <View
        style={{
          marginTop: "25%",
          marginBottom: "15%",
        }}
      >
        <Image
          source={AppImages.icMessage}
          style={{
            height: 27,
            width: 30,
            justifyContent: "center",
            alignSelf: "center",
          }}
        />
        <View
          style={{
            paddingHorizontal:
              deviceSize === DEVICE_SIZES.EXTRA_LARGE_DEVICE ? 20 : 10,
            marginTop: 15,
            justifyContent: "center",
            alignSelf: "center",
          }}
        >
          <Text
            style={[
              deviceSize === DEVICE_SIZES.EXTRA_LARGE_DEVICE
                ? AppFontStyle.REGULAR_ANYBODY_14
                : AppFontStyle.REGULAR_ANYBODY_12,
              { color: AppColors.gray_C3C3C3, textAlign: "center" },
            ]}
          >
            <Text style={{ fontWeight: "bold" }}>Note:</Text> You can only send
            and view messages in the event room after signing-up for the event.
          </Text>
        </View>
      </View>
      <Text
        style={[
          AppFontStyle.REGULAR_ANYBODY_12,
          { color: AppColors.gray_C3C3C3 },
        ]}
      >
        Please remember to communicate respectfully and keep the chat
        appropriate for all users.
      </Text>
    </View>
  );

  const StaticCancelEventMessage = () => (
    <View>
      <View
        style={{
          marginTop: "25%",
          marginBottom: "15%",
        }}
      >
        <Image
          source={AppImages.icMessage}
          style={{
            height: 27,
            width: 30,
            justifyContent: "center",
            alignSelf: "center",
          }}
        />
        <Text
          style={[
            deviceSize === DEVICE_SIZES.EXTRA_LARGE_DEVICE
              ? AppFontStyle.BOLD_ANYBODY_18
              : AppFontStyle.REGULAR_ANYBODY_16,
            {
              marginTop: 15,
              justifyContent: "center",
              alignSelf: "center",
              color: AppColors.gray_C3C3C3,
            },
          ]}
        >
          Event canceled
        </Text>
        <View
          style={{
            paddingHorizontal:
              deviceSize === DEVICE_SIZES.EXTRA_LARGE_DEVICE ? 20 : 10,
            marginTop: 15,
            justifyContent: "center",
            alignSelf: "center",
          }}
        >
          <Text
            style={[
              deviceSize === DEVICE_SIZES.EXTRA_LARGE_DEVICE
                ? AppFontStyle.REGULAR_ANYBODY_14
                : AppFontStyle.REGULAR_ANYBODY_12,
              { color: AppColors.gray_C3C3C3, textAlign: "center" },
            ]}
          >
            {reason}
          </Text>
        </View>
      </View>
    </View>
  );

  const StaticMessageInput = () => (
    <View style={{ flexDirection: "row", marginBottom: 20, marginTop: 10 }}>
      <TextInput
        style={[
          AppFontStyle.REGULAR_ANYBODY_14,
          {
            flex: 1,
            marginRight: 10,
            backgroundColor: "white",
            borderRadius: 10,
            paddingHorizontal: 7,
            paddingVertical: 15,
            justifyContent: "center",
            alignSelf: "center",
            color: "black",
          },
        ]}
        placeholderTextColor="#989898"
        placeholder="Sign up to join the chat..."
        editable={false} // Disabled for preview
      />
      <TouchableOpacity
        style={{
          backgroundColor: AppColors.grayColor_505050, // Disabled style
          height: 45,
          width: 43,
          borderRadius: 10,
          justifyContent: "center",
          alignItems: "center",
        }}
        onPress={() => {
          toast.info("Please sign up to participate in chat");
        }}
        disabled={true}
      >
        <Image source={AppImages.arrowUp} style={{ height: 25, width: 25 }} />
      </TouchableOpacity>
    </View>
  );

  return (
    <View style={styles.chatBox}>
      <View style={[styles.eventView, { width: 500 }]}>
        <Text
          style={[
            deviceSize === DEVICE_SIZES.EXTRA_LARGE_DEVICE
              ? AppFontStyle.BOLD_ANYBODY_24
              : AppFontStyle.REGULAR_ANYBODY_18,
            { marginHorizontal: 5 },
          ]}
        >
          {title ? title : "Chat"}
        </Text>
        <View style={styles.separator} />
        <View style={styles.chatOuterView}>
          <CommonButton
            title="Chat"
            onTap={onChatTap}
            buttonStyle={styles.btnChat}
            textStyle={AppFontStyle.REGULAR_ANYBODY_12}
          />
          <View style={styles.separator} />
          <View style={styles.messageListContainer}>
            {IsEventCanceled ? (
              <StaticCancelEventMessage />
            ) : (
              <>
                {/* Show sample messages or no messages */}
                {sampleMessages.length > 0 ? (
                  <View style={{ padding: 10 }}>
                    {sampleMessages.map((message, index) => (
                      <StaticMessageItem
                        key={index}
                        message={message}
                        isUser={message.isUser}
                      />
                    ))}
                    {/* Add a note that this is preview */}
                    <View
                      style={{
                        marginTop: 20,
                        padding: 10,
                        backgroundColor: AppColors.grayColor_484848,
                        borderRadius: 8,
                        alignItems: "center",
                      }}
                    >
                      <Text
                        style={[
                          AppFontStyle.REGULAR_ANYBODY_12,
                          { color: AppColors.gray_C3C3C3, textAlign: "center" },
                        ]}
                      >
                        💬 Preview Mode
                      </Text>
                      <Text
                        style={[
                          AppFontStyle.REGULAR_ANYBODY_10,
                          {
                            color: AppColors.gray_C3C3C3,
                            textAlign: "center",
                            marginTop: 5,
                          },
                        ]}
                      >
                        Sign up to see real chat messages
                      </Text>
                    </View>
                  </View>
                ) : (
                  <StaticNoMessages />
                )}
              </>
            )}
          </View>
          <View style={styles.separator} />
          <StaticMessageInput />
        </View>
      </View>
    </View>
  );
};

const useStyles = CreateResponsiveStyle(
  {
    eventView: {
      marginBottom: 5,
      alignSelf: "flex-end",
    },
    separator: {
      height: 1,
      backgroundColor: AppColors.grayColor_484848,
      marginVertical: 10,
    },
    messageContainer: {
      padding: 10,
      borderRadius: 10,
      marginVertical: 5,
      minWidth: "40%",
      maxWidth: "60%",
    },
    messageListContainer: {
      minHeight: 350,
      maxHeight: 350,
      minWidth: 350,
      marginVertical: "5%",
    },
    userMessageText: {
      color: AppColors.black,
    },
    adminMessageText: {
      color: AppColors.white,
    },
    header: {
      flexDirection: "row",
      marginBottom: 5,
    },
    userNameText: {
      fontWeight: "bold",
      color: "rgb(237 140 242)",
      maxWidth: "60%",
    },
    timestampText: {
      fontSize: 12,
      color: AppColors.gray_C3C3C3,
    },
    chatBox: {
      marginTop: 20,
      position: "absolute",
      right: 0,
    },
    chatOuterView: {
      margin: 5,
      backgroundColor: AppColors.grayColor_282828,
      borderRadius: 12,
      paddingHorizontal: 10,
      marginVertical: 10,
    },
    btnChat: {
      paddingVertical: 0,
      paddingHorizontal: 15,
      backgroundColor: AppColors.appViolentColor_6D08C3,
      shadowOpacity: 0,
      height: 40,
      marginTop: 10,
      borderRadius: 7,
    },
  },
  {
    [DEVICE_SIZES.LG]: {
      chatBox: {
        marginTop: 20,
        position: "relative",
        right: 0,
        bottom: -0,
      },
    },
    [DEVICE_SIZES.MD]: {
      chatBox: {
        marginTop: 20,
        position: "relative",
        right: 0,
        bottom: -0,
      },
      messageListContainer: {
        minHeight: 350,
        maxHeight: 350,
        maxWidth: 350,
        minWidth: 300,
        marginVertical: "5%",
      },
    },
    [DEVICE_SIZES.SM]: {
      chatBox: {
        marginTop: 20,
        position: "relative",
        right: 0,
        bottom: -0,
      },
      messageListContainer: {
        minHeight: 350,
        maxHeight: 350,
        maxWidth: 350,
        minWidth: 250,
        marginVertical: "5%",
      },
      eventView: {
        marginTop: 5,
        marginBottom: 5,
        alignSelf: "center",
      },
    },
    [DEVICE_SIZES.XS]: {
      chatBox: {
        marginTop: 20,
        position: "relative",
        right: 0,
        bottom: -0,
      },
      messageListContainer: {
        minHeight: 350,
        maxHeight: 350,
        maxWidth: 350,
        minWidth: 200,
        marginVertical: "5%",
      },
      eventView: {
        marginTop: 5,
        marginBottom: 5,
        alignSelf: "center",
      },
    },
  }
);

export default StaticEventChatPreview;
